import { Injectable, EventEmitter } from '@angular/core';
import { BackendService } from '@tg4/http-infrastructure/dist/src';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { LoadingController, Platform, ToastController } from '@ionic/angular';
import { TranslationService } from './translation.service';
import { environment } from '../../../environments/environment';

@Injectable()
export class SharedService {

  public showLoader$ = new EventEmitter();

  private deploy?: any;

  constructor(
    private _platform: Platform,
    protected _toastController: ToastController,
    protected _translationService: TranslationService,
    private _loadingController: LoadingController,
  ) {
    // Load Deploy plugin only when running on Capacitor/Hybrid
    if (this._platform.is('capacitor')) {
      try {
        // Capacitor bridges Cordova plugins under window.IonicCordova.deploy
        this.deploy = (window as any).IonicCordova?.deploy ?? undefined;
      } catch {
        this.deploy = undefined;
      }
    }
  }

  public getLoaderSubject() {
    return this.showLoader$;
  }

  public setLoaderValue(value: boolean) {
    this.showLoader$.next(value);
  }

  public downloadFile(res: any, options?: { defaultFileName?: string }) {
    const blob: Blob = res.body;
    const contentDisposition = res.headers ? res.headers.get('content-disposition') : '';
    const filename = contentDisposition && contentDisposition.split(';') ?
      contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim() :
      options.defaultFileName;

    if (this._platform.is('capacitor')) {
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = btoa(reader.result as string);
        Filesystem.writeFile({
          data: b64,
          path: filename,
          directory: Directory.Documents
        });
        const toast = await this._toastController.create({
          message: this._translationService.getTranslation('Report.Success'),
          position: 'top',
          color: 'success',
          duration: 3000
        });
        toast.present();
      };
      reader.readAsBinaryString(blob);
    } else {
      const url = window.URL.createObjectURL(blob);
      const element = document.createElement('a');
      element.href = url;
      element.download = filename;
      document.body.appendChild(element);
      element.click();
    }
  }

  public async liveUpdate(): Promise<void> {
    if (!this.deploy) {
      return; // Web context: skip live update
    }

    try {
      await this.deploy.configure({ appId: environment.appflow.appId, channel: environment.appflow.channel});
      const update = await this.deploy.checkForUpdate();

      if (update.available) {
        const loading = await this._loadingController.create({ message: 'updating 0%'});
        loading.present();

        await this.deploy.downloadUpdate((progress) => {
          loading.message = 'updating ' + progress + '%';
        });
        await this.deploy.extractUpdate((progress) => {
          loading.message = 'extracting ' + progress + '%';
        });

        loading.dismiss();
        await this.deploy.reloadApp();
      }
    } catch (ex) {
      const toast = await this._toastController.create({
        message: 'Error while updating app',
        position: 'top',
        color: 'danger',
        duration: 3000
      });
      toast.present();
    }
  }
}
